"use client";

import { ANIMATION_START_Y } from "@utils/animation";
import { showOnlineExclusiveFeatureFlag } from "@utils/default";
import { useLiveQuery } from "dexie-react-hooks";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useSearchParams } from "next/navigation";
import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";

// import Lottie from "react-lottie";
import FilterAction from "@/components/ui/Form/Filters/FilterAction";
import FilterComponent from "@/components/ui/Form/Filters/FilterComponent";
import FilterLabel from "@/components/ui/Form/Filters/FilterLabel";
import FilterSeparator from "@/components/ui/Form/Filters/FilterSeparator";
import { SEO_SALE_PAGE, parseFilters } from "@/components/ui/Form/Filters/FilterUtils";
import { cleanParams } from "@/components/ui/Form/Filters/FilterUtils";
import Switch from "@/components/ui/Form/Switch/Switch";
import Toggle from "@/components/ui/Form/Toggle/Toggle";
import Icon from "@/components/ui/Icon/Icon";
import Modal from "@/components/ui/Modal/Modal";
import ModalContent from "@/components/ui/Modal/ModalContent";
import { ModalsContext } from "@/context/ModalsContext";
import { DEFAULT_TYPE, SearchContext } from "@/context/SearchContext";
import { UserContext } from "@/context/UserContext";
import { clearAllTracking, clearAllTrackingEvent } from "@/dataLayer/dataLayer";
import { showAllUnitsClick } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useMediaQuery from "@/hooks/useMediaQuery";
import { PRODUCT_TYPE, SearchParams } from "@/types";

import CustomLoader from "../../CustomLoader/CustomLoader";
import Dropdown from "../../Dropdown/Dropdown";
// import animationData from "../../../../public/animations/dots.json";
import { locationsFromID } from "./components/Autocomplete/AutocompleteUtils";

interface Props {
  name: string;
  filters: string[];
  control: Control<any>;
  isHomepage?: boolean;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  resetHandler?: () => void;
  submitHandler?: () => void;
}

const FilterPanel: FC<Props> = ({ filters, name, control, setValue, watch, resetHandler, submitHandler, isHomepage }) => {
  const t = useTranslations();
  const { isMd } = useBreakPoint("md");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const pathname = usePathname();
  const isSearchPage = pathname?.includes("/search/") || false;
  const { settings } = useContext(ModalsContext);
  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const { params, counterTotal, dispatch, counterParams, isLoading, counterDispatch, type, setType } = useContext(SearchContext);
  const searchParams = useSearchParams();
  const locale = useLocale();
  const [language, _] = locale.split("-");
  const { currency, measurement, changeCurrencyAndMeasurement } = useContext(UserContext);
  const dbValues = useLiveQuery(async () => {
    return await parseFilters(params?.type ?? DEFAULT_TYPE, filters, currency, measurement);
  }, [params, currency, measurement]);
  const [projectOnlyAvailable, setProjectOnlyAvailable] = useState<boolean>(true);
  const hasParams = useMemo(() => {
    let cleanedCounterParams = cleanParams(counterParams);
    if (
      Object.keys(cleanedCounterParams).length === 1 &&
      (cleanedCounterParams?.type === "project" || cleanedCounterParams.type === "propertyType")
    ) {
      cleanedCounterParams = {};
    }
    return Object.keys(cleanedCounterParams).length > 0;
  }, [counterParams]);
  const buttonVariation = {
    active: { backgroundColor: "#000", stroke: "#fff" },
    inactive: { backgroundColor: `${isMobile && !isSearchPage ? "transparent" : "#fff"}`, stroke: "#000" },
  };
  const handleClearTracking = async (params: SearchParams, searchTerm: string | number) => {
    let location = "All location";
    if (params.category) {
      location = (await locationsFromID(params.category as string[])).join(",");
    }
    clearAllTrackingEvent(
      location,
      Array.isArray(params.propertyType) ? params.propertyType.join(", ") : params.propertyType ?? "Any",
      Array.isArray(params.bedrooms) ? params.bedrooms.join(", ") : params.bedrooms ?? "Any",
      Array.isArray(params.price) ? params.price.join(", ") : params.price ?? "Any",
      searchTerm,
    );
  };

  useEffect(() => {
    const onlyAvailableParam = searchParams.get("onlyAvailable");
    if (onlyAvailableParam) {
      setProjectOnlyAvailable(true);
      setValue("onlyAvailable", true);
    } else {
      setProjectOnlyAvailable(false);
      setValue("onlyAvailable", false);
    }
  }, [searchParams]);

  const handleFilterSelection = async (params: SearchParams, searchTerm: string | number) => {
    let location = "All location";
    if (params.category) {
      location = (await locationsFromID(params.category as string[])).join(",");
    }
    showAllUnitsClick(
      location,
      Array.isArray(params.propertyType) ? params.propertyType.join(", ") : params.propertyType ?? "Any",
      Array.isArray(params.bedrooms) ? params.bedrooms.join(", ") : params.bedrooms ?? "Any",
      Array.isArray(params.price) ? params.price.join(", ") : params.price ?? "Any",
      searchTerm,
    );
  };

  const handleCurrencAndMeasurement = (item: any, type: any) => {
    if (type === "price") changeCurrencyAndMeasurement(item, measurement);
    else changeCurrencyAndMeasurement(currency, item);
  };
  return (
    <>
      <motion.button
        type="button"
        onClick={() => setPanelOpen(true)}
        initial="close"
        variants={buttonVariation}
        aria-label="filter-button"
        animate={hasParams ? "active" : "inactive"}
        whileHover={hasParams ? buttonVariation.inactive : buttonVariation.active}
        className={`basis-xxl flex-shrink-0 relative aspect-square border ${isMobile && !isSearchPage ? "border-white" : ""} w-xxl flex items-center justify-center rounded-full`}
      >
        <Icon size="24" name={isHomepage ? "filterwhite" : "filters"} title={isHomepage ? "filters" : "sliders"} />
        <AnimatePresence>
          {hasParams && (
            <motion.span
              initial={{ y: ANIMATION_START_Y, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: ANIMATION_START_Y, opacity: 0 }}
              className="bg-gold w-s h-s block absolute -right-3xs top-3xs rounded-full"
            ></motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <Modal open={panelOpen} onClose={() => setPanelOpen(false)}>
        {/* we are increased the width of panel for DE local  */}
        <ModalContent
          removePadding
          className={`w-full md:w-${language === "de" || language === "fr" ? 6 : 5}/12 h-dvh md:h-[90dvh] rounded-none rounded-t-none`}
        >
          <div className="flex flex-col h-dvh md:h-[90dvh]">
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ backgroundColor: "rgba(255,255,255,0.5)" }}
                className="absolute left-0 top-0 w-full h-full z-[100]  flex items-center justify-center rounded-xl"
              >
                <div>
                  {/* <Lottie
                    options={{
                      loop: true,
                      autoplay: true,
                      animationData: animationData,
                      rendererSettings: {
                        preserveAspectRatio: "xMidYMid slice",
                      },
                    }}
                    width={100}
                  /> */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{ backgroundColor: "rgba(255,255,255,0.5)" }}
                    className="fixed left-0 top-0 w-full h-full z-[100]  flex items-center justify-center rounded-xl"
                  >
                    <CustomLoader />
                  </motion.div>
                </div>
              </motion.div>
            )}
            <div className="flex justify-between items-center p-m border-b border-b-gray-10">
              <h3 className="font-primary text-s uppercase">{t("label.filters.title")}</h3>
              <button type="button" onClick={() => setPanelOpen(false)}>
                <Icon name="close" size="20" className="stroke-black" />
              </button>
            </div>
            <div className="md:hidden py-m pr-m flex flex-col gap-s">
              <div className="flex justify-between">
                <FilterLabel asButton={false} label={t(`filter.label.type`)} />
                {/* <Switch
                  value={searchParams.get("type") || PRODUCT_TYPE.PROPERTY_TYPE}
                  // onChange={(e) => dispatch({ action: "SEARCH", payload: { type: e.currentTarget.value } })}
                  onChange={(e) => {
                    const payload: any = { type: e.currentTarget.value };
                    if (e.currentTarget.value !== PRODUCT_TYPE.PROJECT) {
                      payload["onlyAvailable"] = "";
                    } else {
                      payload["onlyAvailable"] = true;
                    }
                    dispatch({ action: "SEARCH", payload: payload });

                    // setValue("type", payload?.type || "project");
                    setValue?.("type", payload.type); // Update form state
                    setValue?.("onlyAvailable", payload.onlyAvailable);
                  }}
                  name="type"
                  values={[PRODUCT_TYPE.PROJECT, PRODUCT_TYPE.PROPERTY_TYPE]}
                  labels={[t("filter.label.project"), t("filter.label.typeSwitch")]}
                /> */}
                <Switch
                  value={searchParams.get("type") || type}
                  onChange={(e) => {
                    const payload: any = { type: e.currentTarget.value };
                    if (e.currentTarget.value !== PRODUCT_TYPE.PROJECT) {
                      payload["onlyAvailable"] = "";
                    } else {
                      payload["onlyAvailable"] = true;
                    }
                    dispatch({ action: "SEARCH", payload: payload });
                    setType(e.currentTarget.value);
                  }}
                  name="type"
                  values={[PRODUCT_TYPE.PROJECT, PRODUCT_TYPE.PROPERTY_TYPE]}
                  labels={[t("filter.label.project"), t("filter.label.typeSwitch")]}
                />
              </div>
              {/* <div className="flex justify-between">
                <FilterLabel asButton={false} label={t("filter.label.onlyAvailable")} />
                <Toggle
                  disabled={searchParams.get("type") !== "project"}
                  label={isMd ? t("filter.label.onlyAvailable1") : ""}
                  name="onlyAvailable"
                  control={control}
                  value={watch("onlyAvailable")?.toString() || "false"}
                  values={["true", "false"]}
                  onChange={(e) => {
                    dispatch({ action: "SEARCH", payload: { onlyAvailable: e.currentTarget.checked } });
                    setValue?.("onlyAvailable", e.currentTarget.checked);
                  }}
                />
              </div> */}
              {isMobile && (
                <div className="flex">
                  <label className={`font-primary text-s uppercase w-full px-m mt-m mb-xs`}>{t("filter.label.onlyAvailable")}</label>
                  <Toggle
                    disabled={searchParams.get("type") !== PRODUCT_TYPE.PROJECT}
                    label={""}
                    name="onlyAvailable"
                    control={control}
                    value={searchParams.get("type") === PRODUCT_TYPE.PROPERTY_TYPE ? "true" : projectOnlyAvailable.toString()}
                    values={
                      searchParams.get("type") === PRODUCT_TYPE.PROPERTY_TYPE
                        ? ["true"] // Only "true" allowed for PROPERTY_TYPE
                        : ["true", "false"] // Allow both for other types
                    }
                    onChange={(e) => {
                      const type = searchParams.get("type");

                      if (type === PRODUCT_TYPE.PROPERTY_TYPE) {
                        // Ignore clicks; no state change or dispatch for PROPERTY_TYPE
                        return;
                      }

                      // Allow toggle for PROJECT type
                      if (type === PRODUCT_TYPE.PROJECT) {
                        setProjectOnlyAvailable(e.currentTarget.checked);
                        dispatch({
                          action: "SEARCH",
                          payload: { onlyAvailable: e.currentTarget.checked },
                        });
                      }
                    }}
                  />
                </div>
              )}
              {isMobile && showOnlineExclusiveFeatureFlag && (
                <div className="flex">
                  <label className={`font-primary text-s uppercase w-full px-m mt-m mb-xs`}>{t("filter.label.onlineExclusive")}</label>
                  <Toggle
                    disabled={searchParams.get("type") !== PRODUCT_TYPE.PROJECT}
                    label={""}
                    name="onlineExclusive"
                    control={control}
                    value={watch("onlineExclusive")?.toString() || "false"}
                    values={["true", "false"]}
                    onChange={(e) => {
                      const newOnlineExclusiveValue = e.currentTarget.checked;
                      setValue("onlineExclusive", newOnlineExclusiveValue);
                      counterDispatch({ action: "COUNT", payload: { onlineExclusive: newOnlineExclusiveValue } });
                    }}
                  />
                </div>
              )}
              <div className="flex justify-between border-b border-b-gray-10 mb-4">
                <FilterLabel asButton={false} label={t("filter.label.sortBy.generic")} />
                <select
                  onChange={(e) => dispatch({ action: "SEARCH", payload: { sort: e.currentTarget.value } })}
                  name="sort"
                  className="font-secondary text-s bg-transparent max-w-[170px]"
                  defaultValue={searchParams.get("sort") || "deliveryDate desc"}
                >
                  <option value="">{t("filter.label.dropdown.selectOption")}</option>
                  <option value="launchDate">
                    {t("filter.label.sortBy.launchDate")} {t("filter.label.sortBy.asc")}
                  </option>
                  <option value="launchDate+desc">
                    {t("filter.label.sortBy.launchDate")} {t("filter.label.sortBy.desc")}
                  </option>
                  <option value="priceMin">
                    {t("filter.label.sortBy.price")} {t("filter.label.sortBy.asc")}
                  </option>
                  <option value="priceMin+desc">
                    {t("filter.label.sortBy.price")} {t("filter.label.sortBy.desc")}
                  </option>
                </select>
              </div>
              <FilterSeparator />
            </div>

            <div className="flex-1 overflow-auto custom-scrollbar flex flex-col gap-s">
              {dbValues &&
                filters.map((key) => {
                  const filterName = `${name}-filter-${key}`;
                  if (!showOnlineExclusiveFeatureFlag && key === "onlineExclusive") {
                    return null;
                  }
                  if (isMobile && key === "onlineExclusive") {
                    return null;
                  }
                  return (
                    dbValues[key]?.length >= 1 && (
                      <div
                        className={`md:px-l ${key === "onlineExclusive" || key === "webIE" || key === "vTour" ? "flex justify-between items-center" : ""}`}
                        key={filterName}
                      >
                        <div className={`${["price", "area"].includes(key) ? "mb-xl flex justify-between" : ""}`}>
                          <FilterLabel
                            asButton={false}
                            label={
                              key === "status"
                                ? t(`filter.label.${key}.status`)
                                : ["others", "isFurnished"].includes(key)
                                  ? t(`filter.label.${key}.${key}`)
                                  : t(`filter.label.${key}`)
                            }
                          />
                          {key === "price" && (
                            <Dropdown
                              name="price"
                              options={settings?.currencies || []}
                              onChange={handleCurrencAndMeasurement}
                              className={"mr-4 font-primary text-s "}
                              defaultValue={currency}
                            />
                          )}
                          {key === "area" && (
                            <Dropdown
                              name="area"
                              options={settings?.measurements || []}
                              onChange={handleCurrencAndMeasurement}
                              className={"mr-4 font-primary text-s"}
                              defaultValue={measurement}
                            />
                          )}
                        </div>
                        <div className={`${["price", "area"].includes(key) ? "w-11/12 md:w-5/6 mx-auto" : ""}`}>
                          <FilterComponent control={control} name={name} filterKey={key} setValue={setValue} watch={watch} values={dbValues[key]} />
                        </div>
                        {/* {key === "onlineExclusive" && <FilterSeparator />} */}
                      </div>
                    )
                  );
                })}
            </div>
            <div className="p-m">
              <FilterAction
                resetHandler={resetHandler}
                submitHandler={submitHandler}
                setDisplayFilters={() => setPanelOpen(false)}
                fullWidth
                submitDisabled={counterTotal === 0}
                submitLabel={t("button.label.showAllUnits", { total: Object.keys(cleanParams(counterParams)).length === 0 ? 0 : counterTotal })}
                resetSearchFilters={() => {
                  if (name !== SEO_SALE_PAGE) {
                    handleClearTracking(counterParams, t("button.label.clearAll"));
                    const retainedParams = { type: params.type };
                    dispatch({ action: "CLEAR", payload: retainedParams });
                    counterDispatch({ action: "CLEAR", payload: retainedParams });
                  }
                }}
                submitLabelDatalayer={() => {
                  handleFilterSelection(
                    counterParams,
                    t("button.label.showAllUnits", { total: Object.keys(cleanParams(counterParams)).length === 0 ? 0 : counterTotal }),
                  );
                }}
              />
            </div>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
export default FilterPanel;
